import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  PaperProps,
  Stack,
  styled,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { produce } from "immer";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { throttle } from "throttle-debounce";
import Modal, { CommonModalProps } from "..";
import { assetService, cacheService, searchService, setupService, thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbAssetMarkerTypeInfos } from "../../../Core/Api/Types/db-asset-marker-type-infos";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { OrderByMode } from "../../../Core/Api/Types/order-by-mode";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { SequenceMetadataSegment } from "../../../Core/Api/Types/sequence-metadata-segment";
import { StringMultiByEpisodeMetadataInfos } from "../../../Core/Api/Types/string-multi-by-episode-metadata-infos";
import { TitleManagementMode } from "../../../Core/Api/Types/title-management-mode";
import { GetMetadataById, GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ComputeValueBaseTypeFromMetaBaseType, GetValueBaseTypeFromMetaBaseType } from "../../../Core/Utils/functions";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { SetIsLoading } from "../../../Redux/Reducers/redux-slice-timetool";
import { useAppSelector } from "../../../Redux/redux-store";
import { Distinct } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import RequestCard from "../../Cards/RequestCard";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import FormSection from "../../Forms/FormSection/form-section";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { MetadataInput } from "../../MetadataInput/metadata-input-index";
import { GenerateCompilationRequest } from "../../../Core/Api/Types/generate-compilation-request";
import { useTranslation } from "react-i18next";

export type ModalTimetoolGenerateCompilationElementProps = {
  onGenerated: (segments: SequenceMetadataSegment[]) => void;
};

export type ModalTimetoolGenerateCompilationProps = CommonModalProps<ModalTimetoolGenerateCompilationElementProps>;

const ModalTimetoolGenerateCompilation = React.forwardRef<any, ModalTimetoolGenerateCompilationProps>((props, ref) => {
  const { t } = useTranslation();
  const { onGenerated, closeModal } = props;
  const dispatch = useDispatch();

  const formId = "ModalTimetoolCreateCompilation";
  const formSectionId = "ModalTimetoolCreateCompilation-Section";

  const skipIntroTypeName = "Skip Intro";
  const skipCreditsTypeName = "Skip Credits";

  // utils
  const enqueueSnackbar = useCustomSnackbar();
  const titleTypeMeta = useMemo(() => cacheService.GetMetadata().find((m) => m.tagName === "titleType"), []);
  const audioLanguageMeta = useMemo(() => cacheService.GetMetadata().find((m) => m.tagName === "languageAudio")!, []);
  const episodesKeywordsMeta = useMemo(
    () => cacheService.GetMetadata().find((m) => m.tagName === "episodesKeywords")!,
    []
  );
  const titleMetas = useMemo(
    () =>
      cacheService
        .GetMetadata()
        .filter(
          (x) => x.tagName && ["productionDate", "titleType", "serieSeasonEpisodeTotalNumber"].includes(x.tagName)
        ) as DbMetadataFieldInfos[],
    []
  );

  const tagsOptions: string[] = useMemo(() => {
    if (!episodesKeywordsMeta || !episodesKeywordsMeta.values) return [];

    return (
      episodesKeywordsMeta.values
        .flatMap((x) => x.valueObject!)
        .flatMap((x) => x.value)
        .flatMap((x) => x.value)
        .filter(Distinct)
        .sort((a, b) => a.localeCompare(b)) || []
    );
  }, [episodesKeywordsMeta]);

  const [expandSkipMarkersSection, setExpandSkipMarkersSection] = useState<boolean>(true);
  const [expandParametersSection, setExpandParametersSection] = useState<boolean>(false);
  const [checkAllMarkers, setCheckAllMarkers] = useState<boolean>(false);

  // data
  const [skipMarkerTypes, setSkipMarkerTypes] = useState<DbAssetMarkerTypeInfos[]>([]);

  // search
  const [titleType, setTitleType] = useState("Season"); // TODO : add input for this
  const [searchInput, setSearchInput] = useState("");

  // selected title
  const [inputLoading, setInputLoading] = useState(false);
  const [fetchedTitles, setFetchedTitles] = useState<DbAssetObjectInfos[]>([]);
  const [selectedTitles, setSelectedTitles] = useState<DbAssetObjectInfos[]>([]);
  const selectedTitleIds = useMemo(() => selectedTitles.map((x) => x.id), [selectedTitles]);
  const [titleIdAndContentsIds, setTitleIdAndContentsIds] = useState<Record<number, number[]>>({});
  const [tagsValuesByTitle, setTagsValuesByTitle] = useState<Record<number, string[]>>({});

  // form
  const [tags, setTags] = useState<string[]>([]);
  const [targetDuration, setTargetDuration] = useState<number | undefined>(undefined);
  const [keepFirstIntroOfTitles, setKeepFirstIntroOfTitles] = useState<boolean>(false);
  const [keepLastCreditsOfTitles, setKeepLastCreditsOfTitles] = useState<boolean>(false);
  const [shuffleAssetLists, setShuffleAssetLists] = useState<boolean>(false);
  const [titleManagementMode, setTitleManagementMode] = useState<TitleManagementMode>(TitleManagementMode.Grouped);
  const [orderByMode, setOrderByMode] = useState<OrderByMode>(OrderByMode.MatchingTag);
  const [objectClasses, setObjectClasses] = useState<ObjectClass[]>([ObjectClass.Raw, ObjectClass.Mezz]);
  const [distinctTracksByTitle, setDistinctTracksByTitle] = useState<boolean>(false);
  const [harmonizeTitleDurations, setHarmonizeTitleDurations] = useState<boolean>(true);
  const [takeRandomAssetsIfNotEnoughTagged, setTakeRandomAssetsIfNotEnoughTagged] = useState<boolean>(true);
  const [shuffleTitleList, setShuffleTitleList] = useState<boolean>(false);
  const [audioLanguageValue, setAudioLanguageValue] = useState<PlatformLanguageCode | null>(null);
  const [useCompilationLimit, setUseCompilationLimit] = useState<boolean>(false);
  const [previousCompilationLimit, setPreviousCompilationLimit] = useState<number | null>(3);

  const {
    isSelected,
    select,
    selected: skipMarkersToUseIds,
    selectAll,
    unselectAll,
  } = useMultipleSelection<number>([], skipMarkerTypes?.map((type) => type.id) || []);

  const objectClassesOptions = useMemo(() => Object.keys(ObjectClass).sort(), []);

  const isSectionValid = useAppSelector(GetSectionIsValid(formId, formSectionId));
  const isFormValid = useMemo(() => {
    if (!isSectionValid || selectedTitleIds.length == 0 || !audioLanguageValue) return false;

    const titleIdAndContentsIdsKeys = Object.keys(titleIdAndContentsIds).map((value) => Number(value));
    if (titleIdAndContentsIdsKeys.length === 0) return false;

    for (let i = 0; i < titleIdAndContentsIdsKeys.length; i++) {
      const element = titleIdAndContentsIdsKeys[i];
      //if (!titleIdAndContentsIds[element] || !titleIdAndContentsIds[element].length) return false;
      if (!titleIdAndContentsIds[element]) return false;
      if (!titleIdAndContentsIds[element].length) return false;
    }

    return true;
  }, [isSectionValid, selectedTitleIds, audioLanguageValue, titleIdAndContentsIds]);

  const filteredTagsOptions: string[] = useMemo(() => {
    if (!tagsOptions) return [];
    if (!Object.keys(tagsValuesByTitle).length) return tagsOptions;

    const allTitleTagsValues: string[] = Object.values(tagsValuesByTitle)
      .flatMap((valuesArray) => valuesArray)
      .filter(Distinct);

    return tagsOptions.filter((tag) => allTitleTagsValues.includes(tag));
  }, [tagsOptions, tagsValuesByTitle]);

  // search titles
  const searchTitles = useCallback(
    (searchValue: string) => {
      if (!searchValue || searchValue.isEmpty() /*|| searchValue.length <= 1*/) return;

      setInputLoading(true);
      searchService
        .SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Title,
            hideTitleWithoutAsset: false,
            metadata: [
              {
                $type: ComputeValueBaseTypeFromMetaBaseType(BaseMetadataType.DbStringMetadataValueInfos),
                id: titleTypeMeta?.id || 0,
                fieldType: GetValueBaseTypeFromMetaBaseType(BaseMetadataType.DbStringMetadataValueInfos),
                value: titleType,
                creationTime: new Date().toISOString(),
                updateTime: null,
                fieldName: null,
                fieldLabel: null,
                targetLayer: ObjectLayer.Title,
                valueLabel: null,
              },
            ],
          })
        )
        .then((response) => {
          setInputLoading(false);
          if (!response.success) {
            enqueueSnackbar(response.message || t("alert:error.somethingWentWrong"), "error");
          } else {
            setFetchedTitles(response.results !== null ? response.results : []);
            console.debug("Search found " + response.results?.length + " titles");
          }
        });
    },
    [titleType, titleTypeMeta]
  );

  // throttle
  const throttleSearchTitles = useMemo(
    () =>
      throttle(GlobalConst.SEARCH_THROTTLE_DELAY_MS, searchTitles, {
        noLeading: true,
      }),
    [searchTitles]
  );

  const handleSearchInputChange = (searchValue: string) => {
    setSearchInput(searchValue);
    throttleSearchTitles(searchValue);
  };

  const handleConfirmClick = () => {
    dispatch(SetIsLoading(true));
    let request = new GenerateCompilationRequest({
      titleIdAndContentsIds: titleIdAndContentsIds,
      tags: tags.length > 0 ? tags : null,
      targetDuration,
      audioLanguageCode: audioLanguageValue,
      skipMarkersToUseIds: skipMarkersToUseIds.length > 0 ? skipMarkersToUseIds : null,
      keepFirstIntroOfTitles,
      keepLastCreditsOfTitles,
      titleManagementMode,
      orderByMode: harmonizeTitleDurations ? OrderByMode.None : orderByMode,
      shuffleAssetLists,
      objectClasses: objectClasses.length > 0 ? objectClasses : null,
      distinctTracksByTitle,
      harmonizeTitleDurations,
      takeRandomAssetsIfNotEnoughTagged,
      shuffleTitleList,
      previousCompilationLimit: useCompilationLimit ? previousCompilationLimit : null,
    });

    assetService.GenerateCompilation(request).then((response) => {
      if (!response.success) {
        enqueueSnackbar(response.message || t("alert:error.somethingWentWrong"), "error");
        dispatch(SetIsLoading(false));
      } else {
        dispatch(SetIsLoading(true));
        enqueueSnackbar(t("alert:success.compilationGenerated"), "success");
        onGenerated(response.segments);
        closeModal && closeModal();
      }
    });
  };

  const handleCancelClick = () => {
    closeModal();
  };

  const handleHarmonizeTitleDurationChange = (value: boolean) => {
    if (value) setOrderByMode(OrderByMode.None);
    setHarmonizeTitleDurations(value);
  };

  const handleTitleManagementModeChange = (value: TitleManagementMode) => {
    if (value === TitleManagementMode.Mixed) {
      setKeepFirstIntroOfTitles(false);
      setKeepLastCreditsOfTitles(false);
    } else {
      setKeepFirstIntroOfTitles(true);
      setKeepLastCreditsOfTitles(true);
    }
    setTitleManagementMode(value);
  };

  const handleSelectedTitles = async (value: DbAssetObjectInfos[]) => {
    const titlesIdsToFetch = value.filter((x) => !selectedTitleIds.includes(x.id)).map((title) => title.id);
    const promiseArray = titlesIdsToFetch.map((titleId) => assetService.GetObjectLightById(titleId, 1));

    if (!episodesKeywordsMeta) {
      enqueueSnackbar(t("alert:error.episodesKeywordsMetadataNotFound"), "error");
      return;
    }

    let titleArray: DbAssetObjectInfos[] = [];
    await Promise.all(promiseArray).then((responses) => {
      responses.forEach((response) => {
        if (response.success && response.graph) {
          titleArray.push(response.graph);
          const episodeKeywordsMetaValue = GetMetadataById(response.graph, episodesKeywordsMeta!.id);
          const episodeKeywords = episodeKeywordsMetaValue?.value as StringMultiByEpisodeMetadataInfos[] | null;
          if (episodeKeywords) {
            setTagsValuesByTitle(
              produce((draft) => {
                draft[response.graph!.id] = episodeKeywords.flatMap((x) => x.value).filter(Distinct);
              })
            );
          }
        } else enqueueSnackbar(response.message || t("alert:error.retrievingTitle"), "error");
      });
    });
    setSelectedTitles([...selectedTitles, ...titleArray]);
  };

  const handleOnContentChange = (titleId: number, selectedContentIds: number[]) => {
    const newTitlesAndContens = { ...titleIdAndContentsIds };
    newTitlesAndContens[titleId] = selectedContentIds;
    setTitleIdAndContentsIds(newTitlesAndContens);
  };

  const handleTitleDelete = (titleId: number) => {
    setSelectedTitles([...selectedTitles.filter((x) => x.id != titleId)]);
    const newTitlesAndContents = { ...titleIdAndContentsIds };
    delete newTitlesAndContents[titleId];
    setTitleIdAndContentsIds(newTitlesAndContents);
    const newTagsByTitle = { ...tagsValuesByTitle };
    delete newTagsByTitle[titleId];
    setTagsValuesByTitle(newTagsByTitle);
  };

  const allMarkersChecked = useMemo(() => {
    return skipMarkersToUseIds.length === skipMarkerTypes.length && keepFirstIntroOfTitles && keepLastCreditsOfTitles;
  }, [skipMarkersToUseIds, skipMarkerTypes, keepFirstIntroOfTitles, keepLastCreditsOfTitles]);

  const indeterminateAllMarkersChecked = useMemo(() => {
    return skipMarkersToUseIds.length !== skipMarkerTypes.length || !keepFirstIntroOfTitles || !keepLastCreditsOfTitles;
  }, [skipMarkersToUseIds, skipMarkerTypes, keepFirstIntroOfTitles, keepLastCreditsOfTitles]);

  const handleCheckAll = () => {
    if (!checkAllMarkers) {
      selectAll();
      setKeepFirstIntroOfTitles(true);
      setKeepLastCreditsOfTitles(true);
    } else {
      unselectAll();
      setKeepFirstIntroOfTitles(false);
      setKeepLastCreditsOfTitles(false);
    }
    setCheckAllMarkers(!checkAllMarkers);
  };

  useEffect(() => {
    setupService.GetMarkerTypesByFamilyName("Skip").then((response) => {
      if (response.success) setSkipMarkerTypes(response.markerTypeInfos);
      else enqueueSnackbar(response.message || t("alert:error.fetchingSkipMarkersFamily"), "error");
    });
  }, []);

  return (
    <Modal
      title={t("modal:title.createNewCompilation")}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!isFormValid}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          {/* duration */}
          <FormInputNumber
            label={t("field:label.compilationDuration")}
            stackProps={{ spacing: 0 }}
            value={targetDuration}
            onChange={(e) => setTargetDuration(Number(e.target.value))}
            validationProps={{
              formId: formId,
              formSectionId: formSectionId,
              fieldId: "targetDuration",
              required: true,
              positive: true,
              initialValue: targetDuration,
            }}
          />
          <Stack>
            {/* Titles */}
            <Stack spacing={1}>
              <InputLabel sx={{ mb: 0 }} required>
                {t("field:label.selectTitles")}
              </InputLabel>
              <StyledAutocomplete
                // options
                multiple
                options={fetchedTitles}
                noOptionsText={
                  searchInput.length > 0 ? t("field:helper.noTitlesFound") : t("field:helper.startTypingToSearchTitle")
                }
                loading={inputLoading}
                loadingText={t("field:helper.searchingForTitles")}
                getOptionLabel={(option) =>
                  `${option.titleCustomerId ? `#${option.titleCustomerId} - ` : ""} ${option.name} (${moment(
                    GetMetadataByTagName(option, "productionDate", titleMetas)?.value
                  ).year()})${
                    GetMetadataByTagName(option, "titleType", titleMetas)?.value === "Season"
                      ? ` - ${GetMetadataByTagName(option, "serieSeasonEpisodeTotalNumber", titleMetas)?.value || 0} ep`
                      : ""
                  }`
                } // only display title Name
                getOptionDisabled={(option) => selectedTitleIds.includes(option.id)} // disable options that are already selected (useful in the case we changed search list)
                isOptionEqualToValue={(option) => selectedTitleIds.includes(option.id)} // used to hilight option when new search retrieve same results
                // input - Text entered by user
                inputValue={searchInput}
                onInputChange={(event, value) => handleSearchInputChange(value)}
                filterOptions={(options, object) => options}
                // value - Titles selectioned by user
                value={selectedTitles}
                onChange={(event, value) => handleSelectedTitles(value)}
                renderTags={() => <></>} // use to not display selected values
                // ui props
                disablePortal
                disableClearable // we don't want the user to be able to clear selection
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    endAdornment={
                      <>
                        {inputLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    }
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.searchTitles")}
                    sx={{ width: "100%" }}
                  />
                )}
                popupIcon={
                  <>
                    <Divider orientation="vertical" sx={{ height: 20, mr: 1 }} />
                    <SearchIcon />
                  </>
                }
                sx={{ flex: 1, mb: 1, zIndex: 9999 }}
                slots={{ paper: CustomPaper }}
              />
              {selectedTitles.length > 0 && (
                <Box bgcolor="backgroundAlpha.main" borderRadius="6px" p={2}>
                  {selectedTitles.map((title, index) => (
                    <RequestCard
                      key={index}
                      id={title.name}
                      sx={{ mb: index !== selectedTitles.length - 1 ? 2 : 0 }}
                      cover={thumbnailService.GetObjectPosterUrl(title.id, ThumbnailSize.small)}
                      title={title.name}
                      alreadyAddedToRequest
                      contents={
                        title.childObjects
                          ?.filter(
                            (x) => x.layer === ObjectLayer.Content && x.status === ObjectStatus.New && !x.isDeleted
                          )
                          ?.map((content) => ({
                            name: content.name,
                            id: content.id,
                            childCount: content.childCount || 0,
                          })) || []
                      }
                      onRemoveRequestClick={() => handleTitleDelete(title.id)}
                      onContentChange={(selectedIds) => handleOnContentChange(title.id, selectedIds)}
                    />
                  ))}
                </Box>
              )}
            </Stack>
            {/* Tags */}
            <Box sx={{ pt: 1 }}>
              <InputLabel>{t("common:tags")}</InputLabel>
              <Autocomplete
                options={filteredTagsOptions}
                multiple
                value={tags}
                onChange={(_, value) => setTags(value)}
                renderInput={(params) => (
                  <TextField sx={{ mt: -2, mb: 1 }} {...params} placeholder={t("field:placeholder.addTag")} />
                )}
              />
            </Box>
            <Stack>
              {/* takeRandomAssetsIfNotEnoughTagged */}
              <FormControlLabel
                checked={takeRandomAssetsIfNotEnoughTagged}
                value={takeRandomAssetsIfNotEnoughTagged}
                onChange={(_, value) => setTakeRandomAssetsIfNotEnoughTagged(value)}
                control={<Switch />}
                label={t("field:label.takeRandomAssets")}
              />
              <FormControlLabel
                checked={useCompilationLimit}
                value={useCompilationLimit}
                onChange={(_, value) => setUseCompilationLimit(value)}
                control={<Switch />}
                label={
                  <Typography variant="body1">
                    {t("modal:modalTimetoolGenerateCompilation.label.skipAssetsUsedToRandomlyFill")}{" "}
                    <OutlinedInput
                      type="number"
                      value={previousCompilationLimit}
                      onChange={(e) => setPreviousCompilationLimit(Number(e.target.value))}
                      margin="none"
                      sx={{ width: "60px", p: "0px" }}
                    />{" "}
                    {t("modal:modalTimetoolGenerateCompilation.label.previousCompilations")}
                  </Typography>
                }
              />
            </Stack>
            <Stack spacing={1}>
              <InputLabel required={true}>{t("field:label.audioLanguage")}</InputLabel>
              <MetadataInput
                data={audioLanguageMeta}
                value={audioLanguageValue}
                onChange={(value) => setAudioLanguageValue(value)}
              />
            </Stack>
          </Stack>
        </Stack>
        <FormSection
          icon={<PushPinOutlinedIcon />}
          title={t("modal:title.skipMarkers")}
          defaultExpanded={true}
          expanded={expandSkipMarkersSection}
          onExpand={() => setExpandSkipMarkersSection(!expandSkipMarkersSection)}
        >
          <Stack spacing={2}>
            <OutlinedCheckbox checked={checkAllMarkers} onClick={() => handleCheckAll()} sx={{ flex: 1 }}>
              <Typography>{t("modal:body.checkOrUncheckAll")}</Typography>
            </OutlinedCheckbox>

            <Divider />
            {!skipMarkerTypes.length && <Typography>{t("modal:body.noSkipMarkerFound")}</Typography>}
            <Grid container spacing={1}>
              {skipMarkerTypes.map((type) => (
                <React.Fragment key={type.id}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={<Checkbox checked={isSelected(type.id)} onChange={() => select(type.id)} />}
                      label={type.name}
                      //sx={{ pl: 2.5 }}
                    />
                    {/* <OutlinedCheckbox
                      key={type.id}
                      checked={isSelected(type.id)}
                      onClick={() => select(type.id)}
                      sx={{ flex: 1 }}
                    >
                      <Typography>{type.name}</Typography>
                    </OutlinedCheckbox> */}
                  </Grid>
                  {type.name === skipIntroTypeName && (
                    <Grid item xs={6}>
                      <FormControlLabel
                        checked={keepFirstIntroOfTitles}
                        value={keepFirstIntroOfTitles}
                        onChange={(e, value) => {
                          if (!isSelected(type.id) && value) select(type.id);

                          setKeepFirstIntroOfTitles(value);
                        }}
                        control={<Switch />}
                        label={t("field:label.keepFirstIntroOfTitle")}
                        //sx={{ pt: 1 }}
                      />
                    </Grid>
                  )}
                  {type.name === skipCreditsTypeName && (
                    <Grid item xs={6}>
                      <FormControlLabel
                        checked={keepLastCreditsOfTitles}
                        value={keepLastCreditsOfTitles}
                        onChange={(e, value) => {
                          if (!isSelected(type.id) && value) select(type.id);
                          setKeepLastCreditsOfTitles(value);
                        }}
                        control={<Switch />}
                        label={t("field:label.keepLastCreditsOfTitle")}
                        //sx={{ pt: 1 }}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Stack>
        </FormSection>
        <FormSection
          icon={<SettingsOutlinedIcon />}
          title={t("form:section.title.compilationParameters")}
          defaultExpanded={false}
          expanded={expandParametersSection}
          onExpand={() => setExpandParametersSection(!expandParametersSection)}
        >
          <Stack spacing={1}>
            <Grid container>
              {/* shuffle */}
              <Grid item xs={6}>
                <FormControlLabel
                  checked={shuffleAssetLists}
                  value={shuffleAssetLists}
                  onChange={(_, value) => setShuffleAssetLists(value)}
                  control={<Switch />}
                  label={t("field:label.shuffleSelectedAssets")}
                />
              </Grid>
              {/* shuffleTitleList */}
              <Grid item xs={6}>
                <FormControlLabel
                  checked={shuffleTitleList}
                  value={shuffleTitleList}
                  onChange={(_, value) => setShuffleTitleList(value)}
                  control={<Switch />}
                  label={t("field:label.shuffleTitleList")}
                />
              </Grid>
              {/* distinctTracksByTitle */}
              <Grid item xs={6}>
                <FormControlLabel
                  checked={distinctTracksByTitle}
                  value={distinctTracksByTitle}
                  onChange={(_, value) => setDistinctTracksByTitle(value)}
                  control={<Switch />}
                  label={t("field:label.distinctTracksByTitle")}
                />
              </Grid>
              {/* harmonizeTitleDurations */}
              <Grid item xs={6}>
                <FormControlLabel
                  checked={harmonizeTitleDurations}
                  value={harmonizeTitleDurations}
                  onChange={(_, value) => handleHarmonizeTitleDurationChange(value)}
                  control={<Switch />}
                  label={t("field:label.harmonizeTitleDurations")}
                />
              </Grid>
            </Grid>
            {/* titlemanagement */}
            <Stack spacing={1}>
              <Stack spacing={1} direction="row">
                <InputLabel>{t("field:label.titleManagementMode")}</InputLabel>
                <Tooltip
                  placement="right"
                  arrow
                  title={
                    <Stack spacing={1}>
                      <Stack>
                        <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                          {t("modal:modalTimetoolGenerateCompilation.tooltip.mixed")}
                        </Typography>
                        <Typography variant="body2">{t("modal:body.dontGroupAssetsBytitle")}</Typography>
                      </Stack>
                      <Stack>
                        <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                          {t("modal:modalTimetoolGenerateCompilation.tooltip.grouped")}
                        </Typography>
                        <Typography variant="body2">
                          {t("modal:modalTimetoolGenerateCompilation.tooltip.groupAssetsByTitle")}
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                >
                  <HelpOutlineOutlinedIcon />
                </Tooltip>
              </Stack>
              <FormInputSelect
                //label="Title management mode"
                stackProps={{ spacing: 0 }}
                value={titleManagementMode}
                onChange={(e) => handleTitleManagementModeChange(e.target.value as TitleManagementMode)}
                validationProps={{
                  formId: formId,
                  formSectionId: formSectionId,
                  fieldId: "titleManagementMode",
                  required: false,
                  initialValue: titleManagementMode,
                }}
              >
                {Object.keys(TitleManagementMode).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </FormInputSelect>
            </Stack>
            {/* orderby 
            <Stack spacing={1}>
              <Stack spacing={1} direction="row">
                <InputLabel>Order by mode</InputLabel>
                <Tooltip
                  placement="right"
                  arrow
                  title={
                    <Stack spacing={1}>
                      <Stack>
                        <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                          None :
                        </Typography>
                        <Typography variant="body2">Don't order assets</Typography>
                      </Stack>
                      <Stack>
                        <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                          MatchingTags :
                        </Typography>
                        <Typography variant="body2">
                          Order assets by matching tags. If you use tags this option will lead to more relevant
                          compilation.
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                >
                  <HelpOutlineOutlinedIcon />
                </Tooltip>
              </Stack>
              <FormInputSelect
                //label="Order by mode"
                stackProps={{ spacing: 0 }}
                value={orderByMode}
                onChange={(e) => setOrderByMode(e.target.value as OrderByMode)}
                disabled={harmonizeTitleDurations}
                validationProps={{
                  formId: formId,
                  formSectionId: formSectionId,
                  fieldId: "orderByMode",
                  required: !harmonizeTitleDurations,
                  initialValue: orderByMode,
                }}
              >
                {Object.keys(OrderByMode).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </FormInputSelect>
            </Stack>*/}

            {/* objectClasses */}
            <Stack>
              <InputLabel sx={{ mb: 0 }}>{t("field:label.objectClassesLowerCase")}</InputLabel>
              <Autocomplete
                multiple
                options={objectClassesOptions}
                value={objectClasses}
                onChange={(_, value) => setObjectClasses(value as ObjectClass[])}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.selectObjectClassesLowerCase")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </FormSection>
      </Stack>
    </Modal>
  );
});

export default ModalTimetoolGenerateCompilation;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  ".MuiIconButton-root": {
    transform: "none !important",
    svg: {
      color: `${theme.palette.primary.main} !important`,
    },
  },
})) as typeof Autocomplete;

const CustomPaper = (props: PaperProps) => {
  return <Paper elevation={8} sx={{ zIndex: 9999, ...props.sx }} {...props} />;
};
